/**
 * URLSearchParams-compatible search serialization for TanStack Router.
 *
 * The router's default JSON serialization would change URL shapes
 * (`?status=["A","B"]`, `"2"` → number) and break existing bookmarks and
 * returnUrls. These serializers keep URLs byte-identical to the
 * react-router era: repeated keys become arrays, values stay strings.
 *
 * The `email` value is the one exception, and permanently so: the backend
 * inlines a stored address into the recovery link unescaped, so a bare `+`
 * there belongs to the address rather than standing for a space. The branch
 * in `parseAppSearch` is the standing contract with that link, not a shim to
 * drop once some backend migration lands.
 */
export type AppSearch = Record<string, string | string[]>;

/** First value of a possibly-repeated search param, for validateSearch functions. */
export const first = (value: unknown): string | undefined =>
  Array.isArray(value) ? (value[0] as string | undefined) : (value as string | undefined);

export function parseAppSearch(searchStr: string): AppSearch {
  const params = new URLSearchParams(searchStr);
  const result: AppSearch = {};

  for (const key of new Set(params.keys())) {
    // Decode `email` with any bare '+' pre-escaped: a recovery link carries the address
    // unescaped, so a '+' there is part of it. Sound for this key alone because a space is not
    // legal in an unquoted address — within `email` it could only be a mangled '+' — and no other
    // parameter can make that claim, so `returnUrl` and the rest keep reading '+' as a space.
    // '%2B' has no bare '+' to escape and still decodes to '+' exactly once, so a link written
    // either way resolves to the same address.
    const all = key === 'email' ? new URLSearchParams(searchStr.replace(/\+/g, '%2B')).getAll(key) : params.getAll(key);
    result[key] = all.length > 1 ? all : all[0]!;
  }

  return result;
}

export function stringifyAppSearch(search: Record<string, unknown>): string {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(search)) {
    if (value == null) continue;
    if (Array.isArray(value)) {
      value.forEach((item) => params.append(key, String(item)));
    } else {
      params.set(key, String(value));
    }
  }

  const str = params.toString();
  return str ? `?${str}` : '';
}
